/**
 * Paint for the prospect layers, served from GeoServer WFS.
 *
 * The colours are not chosen here — they are the same ones the report itself
 * draws with (see the pipeline's reference/geojson-schema.md). A report and a
 * map that disagree about which circle is which target is worse than having no
 * map, so if the two ever drift the report wins and this file is the thing to
 * fix.
 *
 * WHY A SOURCE PER LAYER RATHER THAN ONE SOURCE FILTERED ON `kind`.
 *
 * The bucket bundle this replaces was a single FeatureCollection carrying every
 * kind at once, so every layer filtered `['==', ['get','kind'], …]` over one
 * source. GeoServer publishes each category as its OWN view — and those views
 * do not select `kind` at all (only prospect_lines does, because it merges two).
 * Filtering on a column that is not served matches nothing and draws an empty
 * map with no error anywhere. One source per typename is both the honest shape
 * and the one that lets a category be fetched, or skipped, on its own.
 */
export const PROSPECT_TEAL = '#00D9EC';
export const PROSPECT_WHITE = '#FFFFFF';
export const PROSPECT_BLACK = '#000000';
export const PROSPECT_CASING = '#0B0F10';
/**
 * Recovered samples carry the SAME four colours the soil and drill-hole overlays
 * use, at the same thresholds — defaultAssayMArkerColourSettings (ppb) and
 * defaultDrillHoleColourSettings (g/t) in the two query tools. A recovered
 * sample is the same measurement as a digitised one; the only difference is
 * that nobody keyed it in. Reading it needs the value ramp already learnt, not
 * a second colour language.
 *
 * Duplicated as literals rather than imported because those two defaults are
 * user-editable filter state: the overlays re-colour when the user drags a
 * threshold, and a recovered sample must NOT follow — it is not in the filtered
 * set and would then be the only mark on screen whose colour disagreed with its
 * own legend. If the shipped defaults change, change these to match.
 */
export const ASSAY_RAMP = ['#0000FF', '#FFA500', '#FF0000', '#800080'];
/**
 * Drill collars recovered off a scanned plate.
 *
 * Deliberately OUTSIDE the assay ramp: a collar carries no gold value, only a
 * position, so giving it a ramp colour would imply a grade it does not have.
 * Amber reads as "provisional" against the ramp's blue-to-purple and against
 * the findings' black discs.
 */
export const PROSPECT_AMBER = '#E8A33D';
/** ppb, soil: >0 blue, >20 orange, >50 red, >100 purple. */
export const SOIL_STOPS = [0, 20, 50, 100];
/** g/t, drill: >0 blue, >0.1 orange, >1 red, >2.5 purple. */
export const DRILL_STOPS = [0, 0.1, 1, 2.5];

/**
 * The six published views, in draw order. `kind` is what the popup switches on
 * and is NOT a served column — it is stamped onto every feature on arrival so
 * the rest of the component can keep asking what a mark is.
 */
export const CATEGORIES = [
  // One layer per KIND OF THING, not per source report. `drill_holes` carries
  // both collars and their downhole intervals - a row with no ordinal and no
  // from_m is the collar - and `soils` carries both valued samples and sites
  // whose printed value was unreadable.
  //
  // aois, targets and lines are still published (ne:wa_prospect_aois,
  // _targets, _lines) and their layer definitions are kept below, but they are
  // not drawn for now: this overlay is soils, drill holes and findings.
  //
  // `label` is the sidebar's sub-filter text. It lives here, beside the view it
  // names, so a category cannot be drawn on the map without a control to turn it
  // off — which is what would happen if the option list were written out again
  // in the sidebar.
  { key: 'soils', typename: 'ne:wa_prospect_soils', kind: 'soil_sample', label: 'Soils' },
  { key: 'drillholes', typename: 'ne:wa_prospect_drill_holes', kind: 'drill_hole', label: 'Drill holes' },
  { key: 'findings', typename: 'ne:wa_prospect_findings', kind: 'finding', label: 'Findings' },
];

/** The sub-filter's chips, in the order the categories are declared above. */
export const PROSPECT_KIND_OPTIONS = CATEGORIES.map((c) => ({ value: c.key, label: c.label }));

/** Everything on: the overlay's own checkbox is the way to see nothing. */
export const DEFAULT_PROSPECT_KINDS = CATEGORIES.map((c) => c.key);

/**
 * Which categories a filter state selects, as category keys.
 *
 * Undefined filters mean "all" rather than "none": the overlay predates this
 * control, a preset saved before it carries no prospect state, and either would
 * otherwise come back as an empty map with the checkbox on.
 */
export const activeKinds = (filters) => {
  const kinds = filters?.kinds;
  if (!Array.isArray(kinds)) return DEFAULT_PROSPECT_KINDS;
  return DEFAULT_PROSPECT_KINDS.filter((k) => kinds.includes(k));
};

/** The categories those keys select, in draw order. */
export const activeCategories = (filters) => {
  const keys = activeKinds(filters);
  return CATEGORIES.filter((c) => keys.includes(c.key));
};

export const SRC = (key) => `prospect-${key}`;

/**
 * What makes a recovered sample a DOWNHOLE sample.
 *
 * The view carries no medium column, so the medium is inferred from what the
 * row actually holds: a hole name, a depth interval, or a g/t value. `recover
 * --medium drill` fills those and `--medium soil` leaves them null, so this is
 * the same distinction the pipeline drew, read back off the data rather than
 * trusted to a flag nobody writes.
 */
/**
 * A drill_holes row that IS the collar rather than one of its intervals.
 * The table carries both: a row with no downhole ordinal and no printed depth
 * is the hole itself. Intervals share the collar's position, so drawing them
 * too would stack dozens of marks on one point.
 */
/**
 * The gold a drill row actually carries, in ppb.
 *
 * Measured on prod (2026-09-13, 764 rows): au_shallow_ppb and au_deep_ppb are
 * in the schema but populated on NOTHING, so colouring by them paints every
 * hole blank. The value lives on the interval rows - au_ppb on 329 of 437,
 * au_gt on 50 - while the 327 collar rows carry position only and no value at
 * all. So: prefer au_ppb, fall back to au_gt scaled to ppb, and -1 when a row
 * has neither, which is every collar row and is what selects the hollow
 * triangle.
 *
 * au_ppb of 0 is a real reading and must survive this: the emptiness test is
 * on the string form, so only null and absent fall through.
 */
export const DRILL_PPB = ['case',
  ['!=', ['to-string', ['coalesce', ['get', 'au_ppb'], '']], ''], ['to-number', ['get', 'au_ppb']],
  ['!=', ['to-string', ['coalesce', ['get', 'au_gt'], '']], ''], ['*', ['to-number', ['get', 'au_gt']], 1000],
  -1,
];

/**
 * The JS twin of DRILL_PPB, in ppb.
 *
 * It has to agree with that expression exactly. The style uses DRILL_PPB as
 * symbol-sort-key and as the value filter; the fetch uses THIS to choose which
 * row represents a hole on the map, and the popup uses it to print the grade. If
 * the two ever disagree, the map draws one row and the popup describes another.
 * Kept here, beside the expression, so they are read and changed together.
 */
export const drillPpbOf = (q = {}) => {
  const ppb = q.au_ppb;
  if (ppb !== null && ppb !== undefined && ppb !== '') return Number(ppb);
  const gt = q.au_gt;
  if (gt !== null && gt !== undefined && gt !== '') return Number(gt) * 1000;
  return -1;
};

/**
 * HOW DEEP A PROSPECTOR CAN ACTUALLY WORK.
 *
 * The mark's colour describes the first 30 m of the hole and nothing below it.
 * Gold Finder serves surface prospectors — the same reason the production drill
 * table caps at 50 m by design — and a detector, a scrape or a dryblower does
 * not reach past this. Colouring by the whole-hole maximum makes WAB204 the
 * standout on 2365 ppb at roughly 48–52 m: real, the best number on the sheet,
 * and useless to someone deciding where to swing a coil. It also renders the
 * holes that matter — WAB201/202/203, 294–334 ppb in their FIRST interval,
 * 0–4 m — as mid-range marks.
 *
 * This rule existed once, computed server-side by the deleted
 * `_drill_collars_valued` views. The refactor to one source per view replaced it
 * with symbol-sort-key and a comment claiming that "reproduces what the deleted
 * _valued views used to compute". It does not: the sort key lifts the richest
 * interval ANYWHERE in the hole, so the rule was quietly lost and the deep
 * intercepts came back as the brightest marks on the map. It is computed on the
 * client now, in one place, over the rows already fetched.
 *
 * Raised from 20 m to 30 m on 2026-09-21, by request.
 */
export const REACH_M = 30;

/**
 * Tick spacing on the A42187 sections, in metres.
 *
 * No depth is printed beside any value on those sheets, so an interval's depth
 * is inferred from its ordinal — hence "~" and "approx" wherever one is shown.
 * The A45655 tables DO print from/to depths and need none of this. Where a depth
 * is neither printed nor inferable the row is treated as within reach: it cannot
 * be shown to be deep, and dropping a value on a guess is worse than keeping it.
 */
export const TICK_M = 4.03;

/** Where a row starts, in metres: printed if there is one, else inferred. */
export const rowTopDepthM = (p = {}) => {
  const printed = p.from_m;
  if (printed !== null && printed !== undefined && printed !== '') return Number(printed);
  const ordinal = p.ordinal;
  if (ordinal !== null && ordinal !== undefined && ordinal !== '') {
    return (Number(ordinal) - 1) * TICK_M;
  }
  return null;
};

/**
 * Whether a row is workable from surface.
 *
 * The cut errs toward INCLUDING something slightly deeper rather than excluding
 * something shallower: printed values are often fewer than the ticks and the
 * sheet never says which intervals were skipped, so an inferred depth is a
 * lower bound on certainty, not a measurement.
 */
export const withinReach = (p = {}) => {
  const top = rowTopDepthM(p);
  return top === null || top <= REACH_M;
};

export const IS_COLLAR = ['all',
  ['==', ['to-string', ['coalesce', ['get', 'ordinal'], '']], ''],
  ['==', ['to-string', ['coalesce', ['get', 'from_m'], '']], ''],
];

export const HAS_DEPTH = ['any',
  ['all', ['has', 'hole'], ['!=', ['get', 'hole'], null]],
  ['all', ['has', 'from_m'], ['!=', ['get', 'from_m'], null]],
  ['all', ['has', 'au_gt'], ['!=', ['get', 'au_gt'], null]],
];

/**
 * ONE MARK PER HOLE, not one per depth.
 *
 * Every row of a hole shares one point — the collar row and each of its assayed
 * intervals — so drawing the view as it comes stacks them all on that point: 426
 * of 764 rows sit on a shared point and one point carries 43 of them. What you
 * see is the topmost symbol, so the map LOOKS the same either way, but it is
 * forty-three symbols deep, every one of them hit-testable, and the depths it is
 * repeating are exactly what the popup's downhole table is for.
 *
 * `hole_top` is stamped by the fetch (prospectWfs) onto the single row that
 * represents each hole: the richest, which is the one symbol-sort-key was
 * already lifting to the top, so nothing about the drawn map changes. The other
 * rows stay in the source — the popup builds its table from them — they are
 * simply not drawn.
 *
 * NOT `IS_COLLAR`, which is the obvious-looking version of this and is wrong:
 * the 327 collar rows carry position and no value, so that draws the whole layer
 * hollow.
 */
export const IS_HOLE_TOP = ['==', ['get', 'hole_top'], true];

/**
 * Rows carrying a gold value, for the sidebar's "unreadable values" filter.
 *
 * A soil sample whose printed number could not be read is served with au_ppb
 * empty and is drawn WHITE; a drill row with neither au_ppb nor au_gt resolves
 * to -1 and is drawn as the hollow triangle. Both are statements — a sample was
 * taken, a hole was drilled — which is why they are drawn at all, and why hiding
 * them is a choice rather than the default.
 *
 * A measured ZERO is a value and survives both tests: the soil one compares the
 * string form, so only null and absent fall through, and DRILL_PPB returns 0
 * rather than -1 for a genuine 0 ppb.
 */
export const HAS_SOIL_VALUE = ['!=', ['to-string', ['coalesce', ['get', 'au_ppb'], '']], ''];

/**
 * The gold this hole offers within REACH_M, in ppb, or -1 for none.
 *
 * Stamped per hole by the fetch and read here rather than recomputed, so the
 * colour, the sort order and the popup's headline are one number. The mark is
 * coloured by THIS, not by the drawn row's own value: if the row that
 * represents a hole ever changes, the colour still describes the first 30 m.
 */
// The coalesce is INSIDE the to-number, and must stay there: `to-number` turns
// null into 0, so wrapping it the other way round means an unstamped hole reads
// as 0 ppb — the lowest colour band — instead of "nothing to colour". A hollow
// triangle claims nothing; a blue one claims a grade.
export const REACH_PPB = ['to-number', ['coalesce', ['get', 'reach_ppb'], -1]];

/**
 * A hole with an Au value in the first REACH_M metres — something to colour.
 *
 * What the "marks with no value" filter keeps. "Value" here means a value THE
 * MAP CAN SHOW, so the test is the same reach rule the colour uses: a hole
 * assayed only at 88 m has nothing to show, draws hollow, and answers to this
 * filter exactly like a hole that was never assayed at all. Both are marks with
 * no number on them, which is what the reader is choosing to hide.
 *
 * This decides only whether the mark is DRAWN when that option is on. It has no
 * say in the colour: a hole with gold at 88 m and 180 ppb at 12 m is coloured
 * 180 ppb and is not hidden, because it does have a value within reach.
 */
export const HAS_DRILL_VALUE = ['>=', REACH_PPB, 0];


/**
 * A `step` expression over the assay ramp: below the first stop the value is
 * not a measurement we colour, so it falls through to the casing colour.
 *
 * `to-number` guards the string form a value takes once it has been through a
 * vector tile — `step` will not compare a string and fails the whole layer.
 */
export const rampColour = (field, stops) => {
  const expr = ['step', ['to-number', ['coalesce', ['get', field], -1]], PROSPECT_CASING];
  stops.forEach((stop, i) => { expr.push(stop, ASSAY_RAMP[i]); });
  return expr;
};

/** Image id for the recovered-drill triangle in colour band `i`. */
export const drillIcon = (i) => `prospect-drill-${i}`;

/**
 * The outline-only triangle: a hole that is on the plate but carries no value
 * to colour. On the collar and assay views that is a real statement - every
 * interval printed below the source table's cut-off - and it used to be said
 * by drawing the circle hollow. The shape had to keep saying it.
 */
export const DRILL_ICON_EMPTY = 'prospect-drill-empty';

const drillIconStep = (value, stops) => {
  const expr = ['step', value, drillIcon(0)];
  stops.forEach((stop, i) => { expr.push(stop, drillIcon(i)); });
  return expr;
};

/**
 * Pick the triangle for a feature's g/t, as a `step` over the same stops. The
 * images themselves are registered by ProspectOverlay at map load.
 */
export const drillIconName = () => drillIconStep(['to-number', ['coalesce', ['get', 'au_gt'], -1]], DRILL_STOPS);

/**
 * As above, but for the views that serve ppb rather than g/t, and that use the
 * absence of a value as a statement rather than a gap - hence the hollow
 * triangle instead of falling through to the lowest band, which would read as
 * a low value.
 */
export const drillIconValued = (value, stops) => ['case',
  ['<', value, 0], DRILL_ICON_EMPTY,
  drillIconStep(value, stops),
];

/**
 * Draw order WITHIN a layer: the richest hole on top.
 *
 * MapLibre sorts ascending and draws the low keys first, so passing the grade
 * straight through leaves the highest value drawn last, over its neighbours.
 * That matters most exactly where it is hardest to read - a tight recovered
 * grid at 20 m spacing, where whichever mark happens to come last in the WFS
 * response would otherwise cover the one worth seeing.
 *
 * A hole with no value coalesces to -1 and sinks under every real one: the
 * hollow triangle is the least useful mark to have hiding a rich intercept.
 *
 * This orders within one layer only. Across layers ORDER still rules, so a
 * rich collar stays beneath a poor assay - they are separate views drawn in
 * sequence, and merging them is a bigger change than this.
 */
export const gradeSortKey = (field) => ['to-number', ['coalesce', ['get', field], -1]];

/** Shared with the drill-hole overlay's marker, so the two read as one symbol. */
export const DRILL_ICON_LAYOUT = {
  'icon-size': 1.2,
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'icon-anchor': 'center',
};

export const LAYER = {
  aoi: 'prospect-aoi',
  targetFill: 'prospect-target-fill',
  targetLine: 'prospect-target-line',
  targetLabel: 'prospect-target-label',
  corridor: 'prospect-corridor',
  detectorCasing: 'prospect-detector-casing',
  detector: 'prospect-detector',
  collarHalo: 'prospect-collar-halo',
  collar: 'prospect-collar',
  drill: 'prospect-drill',
  soil: 'prospect-soil',
  finding: 'prospect-finding',
  findingLabel: 'prospect-finding-label',
};

/** Every interactive layer, topmost first — the order hit-testing should use. */
export const INTERACTIVE = [
  LAYER.finding, LAYER.drill, LAYER.soil,
];

/**
 * Every layer in draw order, matching the definitions below. useTopLayers
 * re-raises them in this order, so the two must not drift apart — a mismatch
 * would silently reshuffle the paint order rather than fail.
 */
export const ORDER = [
  // The +/-60 m halo is the drill error and belongs under everything it
  // qualifies; the findings are the numbered pins and stay on top.
  LAYER.collarHalo,
  LAYER.soil,
  LAYER.drill,
  LAYER.finding,
  LAYER.findingLabel,
];

/**
 * Layer definitions per category, in draw order: areas, then lines, then the
 * markup, then the numbered pins on top of everything.
 */
export const prospectLayers = {
  aois: () => [{
    id: LAYER.aoi, type: 'line', source: SRC('aois'),
    paint: { 'line-color': '#8A9599', 'line-width': 1, 'line-dasharray': [6, 4], 'line-opacity': 0.7 },
  }],

  targets: () => [
    {
      id: LAYER.targetFill, type: 'fill', source: SRC('targets'),
      // Matches the report's own circle exactly — same hue, same 30%. Somebody
      // reading the report and then the map should not have to learn a second
      // colour language.
      paint: { 'fill-color': PROSPECT_TEAL, 'fill-opacity': 0.3 },
    },
    {
      id: LAYER.targetLine, type: 'line', source: SRC('targets'),
      paint: { 'line-color': PROSPECT_TEAL, 'line-width': 2.4 },
    },
    {
      id: LAYER.targetLabel, type: 'symbol', source: SRC('targets'),
      minzoom: 11,
      layout: {
        // The view does not serve `name`, so the label is the site code the
        // report prints beside the circle — S1, P2. Pairing the map with the
        // report is exactly what that code is for.
        'text-field': ['coalesce', ['get', 'site_id'], ''],
        'text-size': 12,
        // The style's glyph endpoint (demotiles) serves Regular only — asking
        // for Bold renders nothing at all, silently. AmalgamationsPendingOverlay
        // already found this; weight comes from the halo instead.
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-offset': [0, 1.2],
        'text-anchor': 'top',
      },
      paint: { 'text-color': PROSPECT_TEAL, 'text-halo-color': PROSPECT_CASING, 'text-halo-width': 1.6 },
    },
  ],

  lines: () => [
    {
      id: LAYER.corridor, type: 'line', source: SRC('lines'),
      filter: ['==', ['get', 'kind'], 'corridor'],
      // Dashed because it is argued, not surveyed. Do not make this solid.
      paint: { 'line-color': PROSPECT_TEAL, 'line-width': 2.4, 'line-dasharray': [4, 2.5], 'line-opacity': 0.9 },
    },
    {
      id: LAYER.detectorCasing, type: 'line', source: SRC('lines'),
      filter: ['==', ['get', 'kind'], 'detector_line'],
      paint: { 'line-color': PROSPECT_CASING, 'line-width': 4, 'line-opacity': 0.75 },
      layout: { 'line-cap': 'round' },
    },
    {
      id: LAYER.detector, type: 'line', source: SRC('lines'),
      filter: ['==', ['get', 'kind'], 'detector_line'],
      paint: { 'line-color': PROSPECT_WHITE, 'line-width': 2, 'line-dasharray': [2, 1.5] },
      layout: { 'line-cap': 'round' },
    },
  ],

  drillholes: ({ hideEmpty } = {}) => [
    {
      // The +/-60 m accuracy drawn at scale, under the mark. At z16 the halo is
      // ~150 px while the holes sit 20 m apart, so the uncertainty visibly
      // swallows the drill grid rather than resolving into tidy dots.
      id: LAYER.collarHalo, type: 'circle', source: SRC('drillholes'),
      // The halo follows its mark. It is drawn off the COLLAR row, which never
      // carries a grade of its own, so it reads the per-hole reach value that
      // the fetch stamps on every row — without that it would keep a ±60 m
      // amber ring around a hole that is no longer drawn.
      filter: hideEmpty ? ['all', IS_COLLAR, HAS_DRILL_VALUE] : IS_COLLAR,
      paint: {
        'circle-color': PROSPECT_AMBER,
        'circle-opacity': 0.08,
        'circle-stroke-color': PROSPECT_AMBER,
        'circle-stroke-width': 0.5,
        'circle-stroke-opacity': 0.3,
        'circle-radius': [
          '/',
          ['*', ['coalesce', ['to-number', ['get', 'accuracy_m']], 60],
                ['*', ['^', 2, ['zoom']], 512]],
          ['*', 40075016.686,
                ['cos', ['*', ['coalesce', ['to-number', ['get', 'lat']], -30.5],
                         0.017453292519943295]]],
        ],
      },
    },
    {
      // The drill hole itself, as the drill-hole overlay's triangle - same
      // shape and size, black-cased where that one is white, which is what
      // says the value came off a scanned plate rather than an assay table.
      //
      // ONE MARK PER HOLE. Every row of a hole shares one point (verified on
      // prod: 64 multi-row holes, 0 with rows at differing points), so drawing
      // the view as it comes stacked the collar row and every interval row on
      // that point — up to 43 symbols deep — and symbol-sort-key decided which
      // of them you saw. The map looked right and was drawing the same hole over
      // and over, once per depth. IS_HOLE_TOP picks exactly the row that sort
      // key was lifting, so what is drawn does not change; the repeats are what
      // goes. The other rows stay in the source for the popup's downhole table,
      // which is where a hole's depths belong.
      id: LAYER.drill, type: 'symbol', source: SRC('drillholes'),
      filter: hideEmpty ? ['all', IS_HOLE_TOP, HAS_DRILL_VALUE] : IS_HOLE_TOP,
      layout: {
        // Coloured and ordered by the hole's best WITHIN REACH, so a deep
        // intercept can no longer make a hole the brightest mark on ground
        // nobody can work.
        'icon-image': drillIconValued(REACH_PPB, DRILL_STOPS.map((g) => g * 1000)),
        'symbol-sort-key': REACH_PPB,
        ...DRILL_ICON_LAYOUT,
      },
      // Flagged holes - unresolved conflict, provisional label, outside the
      // grid range - used to be drawn at 0.45 here. Every mark is now fully
      // opaque; the flags are still served and still shown in the popup.
    },
  ],

  soils: ({ hideEmpty } = {}) => [{
    // SOIL ramp, not the drill ramp: these are surface samples and 3-43 ppb is
    // exactly the band the soil overlay's 0/20/50/100 stops were built for.
    // Putting them on the drill ramp would collapse the whole traverse into one
    // colour and hide the anomaly, which is the only thing here worth seeing.
    id: LAYER.soil, type: 'circle', source: SRC('soils'),
    // Sites whose printed number could not be read are drawn white; this is the
    // control that takes them off. Omitted rather than set to a pass-everything
    // expression when the option is off, so the layer carries no filter at all —
    // react-map-gl calls setFilter(id, undefined) and MapLibre drops it.
    ...(hideEmpty ? { filter: HAS_SOIL_VALUE } : null),
    // Richest sample on top, unreadable ones underneath everything: a white
    // no-value disc covering a real reading hides the only thing on the
    // traverse worth seeing. `gradeSortKey` coalesces a null to -1, which is
    // below the 0 ppb a genuine below-detection sample carries, so the two do
    // not collapse into each other.
    layout: { 'circle-sort-key': gradeSortKey('au_ppb') },
    paint: {
      // A site with no readable value is filled WHITE, not left hollow and not
      // given the lowest band. It is a sample that was taken and whose number
      // could not be read, which is a different thing from a low result, and a
      // filled mark states that as plainly as a coloured one states a grade.
      'circle-color': ['case',
        ['==', ['to-string', ['coalesce', ['get', 'au_ppb'], '']], ''], PROSPECT_WHITE,
        rampColour('au_ppb', SOIL_STOPS)],
      // Black edge, as every table- or plate-recovered value on this map wears:
      // it was never in an assay table and must not read as a surveyed sample.
      'circle-stroke-color': PROSPECT_BLACK,
      'circle-stroke-width': 1.4,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 11, 3, 14, 5, 17, 7.5],
    },
  }],

  findings: () => [
    {
      // Black disc, white edge. It has to read over pale lake clay and dark
      // ironstone in the same frame, which is why it is not the teal.
      id: LAYER.finding, type: 'circle', source: SRC('findings'),
      paint: {
        'circle-color': PROSPECT_BLACK,
        'circle-stroke-color': PROSPECT_WHITE,
        'circle-stroke-width': 1.6,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 8, 3, 12, 6, 16, 9],
        // Undigitised findings used to be drawn at 0.5. Every mark is now
        // fully opaque; the popup still says it is in no assay table.
      },
    },
    {
      id: LAYER.findingLabel, type: 'symbol', source: SRC('findings'),
      minzoom: 12,
      layout: {
        'text-field': ['coalesce', ['to-string', ['get', 'n']], ''],
        'text-size': 10,
        'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
        'text-allow-overlap': true,
      },
      paint: { 'text-color': PROSPECT_WHITE, 'text-halo-color': PROSPECT_BLACK, 'text-halo-width': 1 },
    },
  ],
};

/** The six finding classes, for the sidebar's sub-filter. */
export const FINDING_CLASSES = [
  ['find', 'Finds'],
  ['working', 'Old workings'],
  ['intercept', 'Drill intercepts'],
  ['anomaly', 'Anomalies'],
  ['geology', 'Geology'],
  ['access', 'Access'],
];
